import React from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  useTheme,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Insights as InsightsIcon,
  WbSunny as WeatherIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ReferenceLine,
} from 'recharts';
import { motion } from 'framer-motion';
import { glassStyle, animationVariants } from '../theme/luxuryTheme';

interface PredictiveInsightsProps {
  selectedLocation?: string;
}

const PredictiveInsights: React.FC<PredictiveInsightsProps> = ({ selectedLocation }) => {
  const theme = useTheme();

  // Mock predictive data
  const nextWeekForecast = [
    { day: 'Mon', predicted: 42000, lastWeek: 38000, confidence: 85 },
    { day: 'Tue', predicted: 38500, lastWeek: 37000, confidence: 82 },
    { day: 'Wed', predicted: 40000, lastWeek: 39000, confidence: 88 },
    { day: 'Thu', predicted: 44000, lastWeek: 41000, confidence: 80 },
    { day: 'Fri', predicted: 52000, lastWeek: 48000, confidence: 75 },
    { day: 'Sat', predicted: 58000, lastWeek: 55000, confidence: 78 },
    { day: 'Sun', predicted: 48000, lastWeek: 46000, confidence: 83 },
  ];

  const demandPredictions = {
    lunchRush: { time: '11:30 AM - 1:30 PM', expectedOrders: 145, staffNeeded: 8 },
    dinnerRush: { time: '5:30 PM - 7:30 PM', expectedOrders: 128, staffNeeded: 7 },
    totalDayOrders: 890,
    peakHour: '12:30 PM',
  };

  const insights = [
    {
      type: 'opportunity',
      title: 'Weather Impact Detected',
      description: 'Sunny weather forecast suggests 15% increase in foot traffic this weekend',
      icon: <WeatherIcon />,
      action: 'Increase inventory by 10%',
    },
    {
      type: 'warning',
      title: 'Staff Shortage Risk',
      description: 'Friday dinner shift currently 2 staff members short based on predicted demand',
      icon: <WarningIcon />,
      action: 'Schedule 2 additional staff',
    },
    {
      type: 'trend',
      title: 'Menu Optimization',
      description: 'Burger sales trending 20% above forecast - consider promotional push',
      icon: <TrendingUpIcon />,
      action: 'Launch burger combo deal',
    },
    {
      type: 'event',
      title: 'Local Event Impact',
      description: 'Concert at nearby venue Saturday - expect 25% increase in late-night orders',
      icon: <EventIcon />,
      action: 'Extend kitchen hours',
    },
  ];

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'opportunity': return theme.palette.success.main;
      case 'warning': return theme.palette.warning.main;
      case 'trend': return theme.palette.info.main;
      case 'event': return theme.palette.primary.main;
      default: return theme.palette.text.primary;
    }
  };

  return (
    <Box>
      <motion.div {...animationVariants.fadeIn}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Predictive Analytics & Insights
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI-powered forecasting and actionable recommendations
          </Typography>
        </Box>
      </motion.div>

      <Grid container spacing={3}>
        {/* Sales Forecast */}
        <Grid item xs={12} lg={8}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.1 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  7-Day Sales Forecast
                </Typography>
                <Chip
                  label="82% Avg Confidence"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    color: theme.palette.success.main,
                  }}
                />
              </Box>
              
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={nextWeekForecast}>
                  <defs>
                    <linearGradient id="predictedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#af2326" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#af2326" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="lastWeekGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#666" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#666" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.05)" />
                  <XAxis dataKey="day" stroke="#A0AEC0" />
                  <YAxis stroke="#A0AEC0" />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box sx={{ ...glassStyle, p: 2, borderRadius: '12px' }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {label}
                            </Typography>
                            {payload.map((entry: any, index: number) => (
                              <Typography key={index} variant="body2" sx={{ color: entry.color }}>
                                {entry.name}: ${entry.value.toLocaleString()}
                              </Typography>
                            ))}
                            {payload[0]?.payload?.confidence && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Confidence: {payload[0].payload.confidence}%
                              </Typography>
                            )}
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="lastWeek"
                    stroke="#666"
                    strokeWidth={1}
                    fillOpacity={1}
                    fill="url(#lastWeekGradient)"
                    name="Last Week"
                  />
                  <Area
                    type="monotone"
                    dataKey="predicted"
                    stroke="#af2326"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#predictedGradient)"
                    name="Predicted"
                  />
                </AreaChart>
              </ResponsiveContainer>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Total Predicted: <strong>$315,500</strong>
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.success.main }}>
                  +8.3% vs Last Week
                </Typography>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        {/* Demand Predictions */}
        <Grid item xs={12} lg={4}>
          <motion.div {...animationVariants.scale} transition={{ delay: 0.2 }}>
            <Card sx={{ ...glassStyle, p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Tomorrow's Demand Forecast
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Avatar sx={{ backgroundColor: 'rgba(175, 35, 38, 0.2)', width: 36, height: 36 }}>
                    <ScheduleIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Expected Orders
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {demandPredictions.totalDayOrders}
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 2, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Lunch Rush
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {demandPredictions.lunchRush.time}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">
                      {demandPredictions.lunchRush.expectedOrders} orders
                    </Typography>
                    <Typography variant="body2" color="warning.main">
                      {demandPredictions.lunchRush.staffNeeded} staff needed
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Dinner Rush
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {demandPredictions.dinnerRush.time}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2">
                      {demandPredictions.dinnerRush.expectedOrders} orders
                    </Typography>
                    <Typography variant="body2" color="warning.main">
                      {demandPredictions.dinnerRush.staffNeeded} staff needed
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Card>
          </motion.div>
        </Grid>

        {/* Actionable Insights */}
        <Grid item xs={12}>
          <motion.div {...animationVariants.fadeIn} transition={{ delay: 0.3 }}>
            <Card sx={{ ...glassStyle, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InsightsIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Actionable Insights
                </Typography>
              </Box>
              
              <Grid container spacing={2}>
                {insights.map((insight, index) => (
                  <Grid item xs={12} sm={6} lg={3} key={index}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.03)',
                        border: `1px solid ${getInsightColor(insight.type)}30`,
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            backgroundColor: `${getInsightColor(insight.type)}20`,
                          }}
                        >
                          {React.cloneElement(insight.icon, {
                            sx: { fontSize: 18, color: getInsightColor(insight.type) }
                          })}
                        </Avatar>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {insight.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {insight.description}
                      </Typography>
                      <Chip
                        label={insight.action}
                        size="small"
                        sx={{
                          backgroundColor: `${getInsightColor(insight.type)}20`,
                          color: getInsightColor(insight.type),
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PredictiveInsights;